/**
 * SvDokumentErstellen — Sachverständigenverfahren: Dokument → PDF in der Akte → E-Mail.
 *
 * Ziel (Wunsch Inhaber): Sobald im SV-Verfahren ein Protokoll/Anschreiben fertig
 * ist, soll daraus eine PDF in der Akte erzeugt und — wenn die Aktendaten
 * vollständig sind — eine E-Mail mit dieser PDF zur Freigabe vorbereitet werden.
 *
 * Umsetzung OHNE neue Abhängigkeiten:
 *  - Das fertige Dokument-HTML liegt in `sv_pdf_queue` (status='pending') zur Akte.
 *  - Hier wird es IM BROWSER via jsPDF + html2canvas (beide bereits Dependencies)
 *    zu einer A4-PDF gerendert.
 *  - Upload in den Supabase-Storage (Bucket 'dokumente'), Eintrag in `dokumente`
 *    (taucht damit in der Akte auf).
 *  - Es wird ein E-Mail-Entwurf (status='ready_for_review') mit der PDF als Anhang
 *    angelegt → erscheint in der Freigabe-Queue. Nichts wird automatisch versendet.
 *
 * Mountpunkt: StickyAktenkopfzeile (nur Gutachten/SV-Verfahren).
 */

import { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

interface Props {
  akte: Record<string, any>;
  onRefetch?: () => void;
}

interface QueueRow { id: string; html: string; filename: string | null; }

const REQUIRED_FIELDS: { keys: string[]; label: string }[] = [
  { keys: ['kennzeichen', 'fahrzeug_kennzeichen'], label: 'Kennzeichen' },
  { keys: ['versicherung'], label: 'Versicherung' },
  { keys: ['schaden_nummer', 'schadennummer'], label: 'Schadennummer' },
  { keys: ['gutachten_sv', 'erteilt_durch'], label: 'Sachverständiger' },
];

function val(akte: Record<string, any>, keys: string[]): string {
  for (const k of keys) {
    const v = akte[k];
    if (v != null && String(v).trim() !== '') return String(v);
  }
  return '';
}

function safeName(s: string): string {
  return (s || 'SV-Dokument').replace(/\.html?$/i, '').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 80);
}

export default function SvDokumentErstellen({ akte, onRefetch }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState<QueueRow | null>(null);
  const [loadingQueue, setLoadingQueue] = useState(false);

  const recipient = val(akte, ['versicherung_email', 'gegner_email', 'empfaenger_email']);

  const missing = REQUIRED_FIELDS.filter(f => !val(akte, f.keys)).map(f => f.label);
  const recipientMissing = !recipient;

  const loadQueue = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const { data } = await supabase
        .from('sv_pdf_queue')
        .select('id, html, filename')
        .eq('akte_id', akte.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setQueue((data as QueueRow) ?? null);
    } catch { setQueue(null); }
    finally { setLoadingQueue(false); }
  }, [akte.id]);

  useEffect(() => { if (open) loadQueue(); }, [open, loadQueue]);

  async function renderToPdfBlob(html: string): Promise<Blob> {
    const holder = document.createElement('div');
    holder.style.position = 'fixed';
    holder.style.left = '-10000px';
    holder.style.top = '0';
    holder.style.width = '794px'; // ~A4 @96dpi
    holder.style.background = '#ffffff';
    holder.innerHTML = html;
    document.body.appendChild(holder);
    try {
      const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
      await pdf.html(holder, {
        autoPaging: 'text',
        margin: [24, 24, 24, 24],
        width: 547,        // A4 content width in pt (595 - 2*24)
        windowWidth: 794,
        html2canvas: { scale: 0.74, useCORS: true, backgroundColor: '#ffffff' },
      });
      return pdf.output('blob');
    } finally {
      document.body.removeChild(holder);
    }
  }

  async function generate() {
    if (!queue?.html) { toast.error('Kein fertiges SV-Dokument hinterlegt.'); return; }
    setBusy(true);
    try {
      const blob = await renderToPdfBlob(queue.html);
      const base = safeName(queue.filename || `SV-Protokoll_${akte.akten_nr || akte.id}`);
      const fileName = `${base}.pdf`;
      const path = `sv-pdf/${akte.id}/${Date.now()}-${fileName}`;

      const up = await supabase.storage.from('dokumente').upload(path, blob, {
        contentType: 'application/pdf', upsert: false,
      });
      if (up.error) throw up.error;

      const { data: dok, error: dokErr } = await supabase.from('dokumente').insert({
        akte_id: akte.id,
        dateiname: fileName,
        dateipfad: path,
        mime_type: 'application/pdf',
        dokument_typ: 'protokoll',
        kategorie: 'sv_dokument',
        dateigroesse: (blob as any).size ?? null,
      }).select('id').single();
      if (dokErr) throw dokErr;

      const { data: draft, error: draftErr } = await supabase.from('email_drafts').insert({
        akte_id: akte.id,
        recipient: recipient || '',
        subject: `Sachverständigenverfahren ${akte.akten_nr || ''} – Protokoll zur Durchsicht`.trim(),
        body_text:
          'Sehr geehrte Damen und Herren,\n\n' +
          'anbei übersenden wir Ihnen das Protokoll des Sachverständigenverfahrens zur Durchsicht.\n\n' +
          'Über eine kurze Rückmeldung per E-Mail freuen wir uns.\n\n' +
          'Mit freundlichen Grüßen',
        status: 'ready_for_review',
        created_by_agent_id: 'sv-dokument',
        review_note: 'SV-Verfahren: Protokoll als PDF erzeugt und angehängt.',
      }).select('id').single();
      if (draftErr) throw draftErr;

      const { error: attErr } = await supabase.from('email_draft_attachments').insert({
        draft_id: (draft as any).id,
        source_type: 'supabase',
        dokument_id: (dok as any).id,
        filename: fileName,
        mime_type: 'application/pdf',
      });
      if (attErr) throw attErr;

      await supabase.from('sv_pdf_queue').update({
        status: 'done', dokument_id: (dok as any).id, processed_at: new Date().toISOString(),
      }).eq('id', queue.id);

      toast.success('PDF in der Akte gespeichert & E-Mail in der Freigabe vorbereitet.');
      setOpen(false);
      onRefetch?.();
    } catch (e: any) {
      toast.error('Fehlgeschlagen: ' + (e?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#0e7490] text-white text-[11px] font-semibold flex-shrink-0 hover:opacity-90 transition-opacity"
        title="SV-Dokument als PDF erzeugen & E-Mail vorbereiten"
        data-testid="sv-dokument-pdf-btn"
      >
        <FileText className="w-3 h-3" /> PDF + E-Mail
      </button>

      {open && (
        <div className="fixed inset-0 z-[130] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
              <FileText className="w-4 h-4 text-[#0e7490]" />
              <span className="text-sm font-semibold text-gray-800 flex-1">SV-Dokument → PDF & E-Mail</span>
              <button onClick={() => !busy && setOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* Vollständigkeits-Check */}
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Vollständigkeit der Akte</div>
                {missing.length === 0 && !recipientMissing ? (
                  <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Alle Pflichtangaben vorhanden.</div>
                ) : (
                  <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      Es fehlen: {[...missing, ...(recipientMissing ? ['E-Mail-Empfänger (Versicherung)'] : [])].join(', ')}.
                      <div className="text-[12px] text-amber-600 mt-0.5">PDF kann trotzdem erzeugt werden; Empfänger ggf. in der Freigabe ergänzen.</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Dokument-Status */}
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Fertiges Dokument</div>
                {loadingQueue ? (
                  <div className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</div>
                ) : queue ? (
                  <div className="flex items-center gap-2 text-sm text-gray-800"><FileText className="w-4 h-4 text-gray-400" /> {queue.filename || 'SV-Dokument'}</div>
                ) : (
                  <div className="text-sm text-gray-500">Noch kein fertiges SV-Dokument hinterlegt. Lass das Protokoll/Anschreiben im Verfahren erstellen — es erscheint dann hier.</div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/50">
              <button
                onClick={generate}
                disabled={busy || !queue}
                className="flex items-center gap-1.5 text-sm font-semibold text-white bg-[#0e7490] px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                PDF erzeugen & E-Mail vorbereiten
              </button>
              <button onClick={() => !busy && setOpen(false)} className="text-sm font-medium text-gray-500 hover:text-gray-800 px-3 py-2 rounded-lg hover:bg-gray-100">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
